import { useState } from "react"
import { flushSync } from "react-dom"

import { prefetchQuery } from "../../../../core/api/queryClient"
import { useMatchEnvironment } from "../../../../modules/environment/envHooks"
import { EnvironmentType } from "../../../../modules/environment/envTypes"

import { fetchOrderChatMessages, OrderChatQuery } from "../orderChatApi"

export const PRINT_CHAT_ENVIRONMENTS = [
    EnvironmentType.Local,
    EnvironmentType.Test,
    EnvironmentType.Demo,
    EnvironmentType.QA,
]

type PrintJob = Readonly<{
    orderId?: number
    withChatHistory: boolean
}>

type PrintedSingleOrder = Required<PrintJob>

export type OrderPrintActions = Readonly<{
    onPrintOrder?: () => void
    onPrintOrderWithChatHistory?: () => void
}>

export function usePrintWithChatHistory(orderIds: ReadonlyArray<number>) {
    const isPrintChatEnabled = useMatchEnvironment(PRINT_CHAT_ENVIRONMENTS)
    const [printJob, setPrintJob] = useState<PrintJob>()

    const print = async (job: PrintJob) => {
        if (job.withChatHistory) {
            await Promise.all(
                (job.orderId === undefined ? orderIds : [job.orderId]).map((orderId) =>
                    prefetchQuery(fetchOrderChatMessages, {
                        queryName: OrderChatQuery.MESSAGES,
                        dependencies: { orderId },
                    }),
                ),
            )
        }
        window.addEventListener("afterprint", () => setPrintJob(undefined), { once: true })
        flushSync(() => setPrintJob(job))
        window.print()
    }

    const getOrderPrintActions = (orderId: number): OrderPrintActions => ({
        onPrintOrder: () => print({ orderId, withChatHistory: false }),
        onPrintOrderWithChatHistory: isPrintChatEnabled ? () => print({ orderId, withChatHistory: true }) : undefined,
    })

    const printedSingleOrder: PrintedSingleOrder | undefined =
        printJob?.orderId === undefined
            ? undefined
            : { orderId: printJob.orderId, withChatHistory: printJob.withChatHistory }

    return {
        isPrintChatEnabled,
        isChatHistoryPrinted: printJob?.orderId === undefined && Boolean(printJob?.withChatHistory),
        printedSingleOrder,
        getOrderPrintActions,
        printWithChatHistory: isPrintChatEnabled ? () => print({ withChatHistory: true }) : undefined,
    }
}
